import apiClient from '../axiosConfig';
import { components } from '../types/openapi';
import type {
  LoginCredentials,
  ExternalAuthCredentials,
  LoginResponse,
  User,
} from '../../Types/Auth/Auth.types';

// Tipos del OpenAPI - usando exactamente los tipos definidos
type Token = components['schemas']['Token'];
type UserResponse = components['schemas']['UserResponse'];
type BodyLoginForAccessToken = components['schemas']['Body_login_for_access_token_api_auth_token_post'];
type AuthFirebaseSerializer = components['schemas']['AuthFirebaseSerializer'];
type CreateForgotPasswordRequest = components['schemas']['CreateForgotPasswordRequest'];
type ForgotPasswordRequest = components['schemas']['ForgotPasswordRequest'];
type ForgotPasswordRequestPassword = components['schemas']['ForgotPasswordRequestPassword'];
type ForgotPasswordResponse = components['schemas']['ForgotPasswordResponse'];
type ResendCodeRequest = components['schemas']['ResendCodeRequest'];

/**
 * Servicio de autenticación
 * Maneja todas las operaciones relacionadas con autenticación y autorización
 */
class AuthService {
  /**
   * Login con usuario y contraseña (OAuth2)
   * Usa exactamente los tipos definidos en OpenAPI
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // Preparar body según el tipo del OpenAPI
    const body: BodyLoginForAccessToken = {
      username: credentials.username,
      password: credentials.password,
      grant_type: credentials.grant_type || null,
      scope: credentials.scope,
      client_id: credentials.client_id || null,
      client_secret: credentials.client_secret || null,
    };

    // Formatear datos según OAuth2 (application/x-www-form-urlencoded)
    const formData = new URLSearchParams();
    formData.append('username', body.username);
    formData.append('password', body.password);
    if (body.grant_type) {
      formData.append('grant_type', body.grant_type);
    }
    if (body.scope) {
      formData.append('scope', body.scope);
    }
    if (body.client_id) {
      formData.append('client_id', body.client_id);
    }
    if (body.client_secret) {
      formData.append('client_secret', body.client_secret);
    }

    // Llamar al endpoint exacto del OpenAPI: POST /api/auth/token
    // Este endpoint SOLO devuelve el token: { access_token: string, token_type: string }
    const response = await apiClient.post<Token>(
      '/auth/token',
      formData.toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    // IMPORTANTE: /auth/token solo devuelve el token, NO devuelve información del usuario
    // El token viene en: { access_token: string, token_type: string }
    const accessToken = response.data.access_token;
    const tokenType = response.data.token_type;
    
    // VALIDAR que el token existe
    if (!accessToken) {
      throw new Error('No se recibió el token de acceso del servidor');
    }
    
    // Debug: verificar que el token se recibió correctamente
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Token recibido de /auth/token:', {
        access_token: accessToken ? `${accessToken.substring(0, 20)}...` : '❌ NO RECIBIDO',
        token_type: tokenType,
        fullResponse: response.data
      });
    }
    
    // Guardar el token ANTES de hacer la llamada a /auth/users/me/
    // para que el interceptor de axios pueda agregarlo a la petición
    localStorage.setItem('token', accessToken);
    
    // Verificar que se guardó correctamente
    const savedToken = localStorage.getItem('token');
    if (process.env.NODE_ENV === 'development') {
      console.log('💾 Token guardado en localStorage:', {
        guardado: !!savedToken,
        coincide: savedToken === accessToken
      });
    }

    // Obtener información del usuario autenticado usando el endpoint del OpenAPI
    // GET /api/auth/users/me/ retorna UserResponse
    // Ahora el token ya está en localStorage y el interceptor lo agregará automáticamente
    let user: UserResponse | undefined;
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('📞 Llamando a /auth/users/me/ con token:', {
          tokenEnLocalStorage: !!localStorage.getItem('token'),
          tokenValue: localStorage.getItem('token')?.substring(0, 20) + '...'
        });
      }
      
      const userResponse = await apiClient.get<UserResponse>('/auth/users/me/');
      user = userResponse.data;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Usuario obtenido de /auth/users/me/:', {
          user,
          role: user?.role,
          roleName: user?.role?.name
        });
      }
    } catch (error: any) {
      console.error('❌ Error al obtener información del usuario:', {
        error,
        status: error.response?.status,
        data: error.response?.data,
        tokenEnLocalStorage: !!localStorage.getItem('token')
      });
      // Si falla, limpiar el token que acabamos de guardar
      localStorage.removeItem('token');
      throw error;
    }

    return {
      access_token: accessToken,
      token_type: tokenType,
      user,
    };
  }

  /**
   * Autenticación externa (Firebase)
   * Usa exactamente los tipos definidos en OpenAPI
   */
  async externalAuth(credentials: ExternalAuthCredentials): Promise<LoginResponse> {
    // Preparar body según el tipo del OpenAPI: AuthFirebaseSerializer
    const body: AuthFirebaseSerializer = {
      token: credentials.token,
    };

    // Llamar al endpoint exacto del OpenAPI: POST /api/auth/external-auth
    const response = await apiClient.post<Token>('/auth/external-auth', body);

    // IMPORTANTE: Guardar el token ANTES de hacer la llamada a /auth/users/me/
    // para que el interceptor de axios pueda agregarlo a la petición
    const accessToken = response.data.access_token;
    
    // VALIDAR que el token existe
    if (!accessToken) {
      throw new Error('No se recibió el token de acceso del servidor');
    }
    
    localStorage.setItem('token', accessToken);

    // Obtener información del usuario autenticado usando el endpoint del OpenAPI
    // GET /api/auth/users/me/ retorna UserResponse
    // Ahora el token ya está en localStorage y el interceptor lo agregará automáticamente
    let user: UserResponse | undefined;
    try {
      const userResponse = await apiClient.get<UserResponse>('/auth/users/me/');
      user = userResponse.data;
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);
      // Si falla, limpiar el token que acabamos de guardar
      localStorage.removeItem('token');
      throw error;
    }

    return {
      access_token: accessToken,
      token_type: response.data.token_type,
      user,
    };
  }

  /**
   * Obtener información del usuario autenticado
   * Usa el endpoint del OpenAPI: GET /api/auth/users/me/
   * Retorna UserResponse según OpenAPI
   */
  async getCurrentUser(): Promise<UserResponse> {
    const response = await apiClient.get<UserResponse>('/auth/users/me/');
    return response.data;
  }

  /**
   * Logout (limpiar tokens del cliente)
   */
  logout(): void {
    // Limpiar tokens del localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('login');
    
    // Opcional: llamar al backend para invalidar el token
    // await apiClient.post('/auth/logout');
  }

  /**
   * Solicitar recuperación de contraseña
   * Usa el endpoint del OpenAPI: POST /api/auth/forgot
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    const body: CreateForgotPasswordRequest = { email };
    try {
      const response = await apiClient.post<ForgotPasswordResponse>(
        '/auth/forgot',
        body
      );
      return response.data;
    } catch (error: any) {
      // Log detallado del error para debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Error en forgotPassword:', {
          url: `${apiClient.defaults.baseURL}/auth/forgot`,
          method: 'POST',
          body,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
      }
      throw error;
    }
  }

  /**
   * Reenviar código de recuperación
   * Usa el endpoint del OpenAPI: POST /api/auth/forgot/resend
   */
  async resendCode(id: number): Promise<ForgotPasswordResponse> {
    const body: ResendCodeRequest = { id };
    const response = await apiClient.post<ForgotPasswordResponse>(
      '/auth/forgot/resend',
      body
    );
    return response.data;
  }

  /**
   * Verificar código de recuperación
   * Usa el endpoint del OpenAPI: POST /api/auth/forgot/check
   */
  async verifyCode(id: number, code: string): Promise<ForgotPasswordResponse> {
    const body: ForgotPasswordRequest = { id, code };
    const response = await apiClient.post<ForgotPasswordResponse>(
      '/auth/forgot/check',
      body
    );
    return response.data;
  }

  /**
   * Cambiar contraseña con código de recuperación
   * Usa el endpoint del OpenAPI: POST /api/auth/forgot/change-password
   */
  async resetPassword(id: number, password: string): Promise<ForgotPasswordResponse> {
    const body: ForgotPasswordRequestPassword = { id, password };
    const response = await apiClient.post<ForgotPasswordResponse>(
      '/auth/forgot/change-password',
      body
    );
    return response.data;
  }

  /**
   * Verificar si hay un token válido en localStorage
   */
  hasToken(): boolean {
    return !!localStorage.getItem('token');
  }

  /**
   * Obtener token del localStorage
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Guardar token en localStorage
   */
  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  /**
   * Obtener usuario del localStorage
   */
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Guardar usuario en localStorage
   */
  setStoredUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }
}

export default new AuthService();

